import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtDT } from "@/lib/format";
import { Bell } from "lucide-react";

export function NotificationBell() {
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const ref = useRef(null);
  const navigate = useNavigate();

  const loadCount = async () => {
    try {
      const r = await api.get("/notifications/unread-count");
      setCount(r.data.count);
    } catch (err) { console.warn("unread-count fetch failed (transient)", err?.message); }
  };

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onDocClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const toggle = async () => {
    if (!open) {
      const r = await api.get("/notifications");
      setItems(r.data);
    }
    setOpen(!open);
  };

  const openItem = async (n) => {
    await api.post("/notifications/read", { ids: [n.notif_id] });
    setOpen(false);
    loadCount();
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    await api.post("/notifications/read", { all: true });
    setItems((arr) => arr.map((n) => ({ ...n, read: true })));
    setCount(0);
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={toggle} className="relative p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors" data-testid="notif-bell">
        <Bell className="w-5 h-5" />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full bg-signal-red text-white text-[9px] font-bold flex items-center justify-center" data-testid="notif-badge">
            {count > 99 ? "99+" : count}
          </span>)}
      </button>
      {open && (
        <div className="absolute right-0 top-11 w-96 max-h-96 overflow-y-auto bg-card border border-border rounded-lg shadow-xl z-50" data-testid="notif-dropdown">
          <div className="flex items-center justify-between px-3 py-2 border-b border-border">
            <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Notifications</p>
            <button className="text-xs text-primary" onClick={markAll} data-testid="notif-mark-all">Mark all read</button>
          </div>
          {items.length === 0 && <p className="text-xs text-muted-foreground px-3 py-4">No notifications</p>}
          {items.map((n) => (
            <button key={n.notif_id} onClick={() => openItem(n)}
              className={`w-full text-left px-3 py-2.5 border-b border-border/40 hover:bg-accent/50 transition-colors ${n.read ? "opacity-60" : ""}`}
              data-testid={`notif-item-${n.notif_id}`}>
              <p className="text-xs font-medium">{n.title}</p>
              {n.message && <p className="text-[11px] text-muted-foreground truncate">{n.message}</p>}
              <p className="text-[10px] text-muted-foreground mt-0.5">{fmtDT(n.created_at)}</p>
            </button>))}
        </div>)}
    </div>
  );
}
